import { useNavigation, useRoute } from '@react-navigation/native'
import React, { useEffect } from 'react'
import styled from 'styled-components/native'

import { extractApiErrorMessage } from 'api/apiHelpers'
import { MaintenancePageType, SubscriptionStep } from 'api/gen'
import { useAuthContext } from 'features/auth/context/AuthContext'
import { useShowDisableActivation } from 'features/forceUpdate/helpers/useShowDisableActivation'
import { QuitIdentityCheckModal } from 'features/identityCheck/components/modals/QuitIdentityCheckModal'
import { logIdentityCheckUnavailable } from 'features/identityCheck/pages/helpers/logIdentityCheckUnavailable'
import { useSetSubscriptionStepAndMethod } from 'features/identityCheck/pages/helpers/useSetCurrentSubscriptionStep'
import { useStepperInfo } from 'features/identityCheck/pages/helpers/useStepperInfo'
import { IdentityCheckStep } from 'features/identityCheck/types'
import { UseNavigationType, UseRouteType } from 'features/navigation/navigators/RootNavigator/types'
import { getSubscriptionHookConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionHookConfig'
import { getSubscriptionPropConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionPropConfig'
import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { analytics } from 'libs/analytics/provider'
import { eventMonitoring } from 'libs/monitoring/services'
import { recordProfileCompletionStart } from 'libs/reviewInApp/creditReviewTrigger'
import { hasOngoingCredit } from 'shared/user/getAvailableCredit'
import { useModal } from 'ui/components/modals/useModal'
import { StepButton } from 'ui/components/StepButton/StepButton'
import { StepButtonState } from 'ui/components/StepButton/types'
import { StepList } from 'ui/components/StepList/StepList'
import { Banner } from 'ui/designSystem/Banner/Banner'
import { BannerType } from 'ui/designSystem/Banner/enums'
import { Button } from 'ui/designSystem/Button/Button'
import { showErrorSnackBar } from 'ui/designSystem/Snackbar/snackBar.store'
import { Page } from 'ui/pages/Page'
import { Invalidate } from 'ui/svg/icons/Invalidate'
import { getSpacing, Spacer, Typo } from 'ui/theme'
import { getTextSemanticAttrs } from 'ui/theme/typographyAttrs/getTextSemanticAttrs'

export const Stepper = () => {
  useShowDisableActivation()
  const { navigate } = useNavigation<UseNavigationType>()
  const { params } = useRoute<UseRouteType<'Stepper'>>()

  const {
    stepsDetails: steps,
    title: stepperTitle,
    subtitle: stepperSubtitle,
    errorMessage,
  } = useStepperInfo()

  const currentStepIndex = steps.findIndex(
    (step) => step.stepState === StepButtonState.CURRENT || step.stepState === StepButtonState.RETRY
  )
  const stepToComplete = steps[currentStepIndex]

  const { subscription } = useSetSubscriptionStepAndMethod()
  const { refetchUser, user } = useAuthContext()

  const { visible, showModal: showQuitIdentityCheckModal, hideModal } = useModal(false)

  useEffect(() => {
    const showMaintenance = () => {
      if (subscription?.nextSubscriptionStep === SubscriptionStep.maintenance) {
        logIdentityCheckUnavailable({ source: 'Stepper', user, subscription, params })
        navigate(
          ...getSubscriptionHookConfig('IdentityCheckUnavailable', {
            withDMS: subscription?.maintenancePageType === MaintenancePageType['with-dms'],
          })
        )
      }
    }
    showMaintenance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription])

  useEffect(() => {
    if (subscription?.nextSubscriptionStep === null) {
      refetchUser()
        .then(({ data: userProfile }) => {
          const hasUserOngoingCredit = userProfile ? hasOngoingCredit(userProfile) : false
          if (hasUserOngoingCredit) {
            navigate(...getSubscriptionHookConfig('BeneficiaryAccountCreated'))
          }
        })
        .catch((error) => {
          eventMonitoring.captureException(
            'Stepper - refetchUser after subscription step completion',
            { level: 'info', extra: { params, error, user, subscription } }
          )
          showErrorSnackBar(extractApiErrorMessage(error))
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription])

  useEffect(() => {
    if (params?.from && stepToComplete?.name) {
      void analytics.logStepperDisplayed(params.from, stepToComplete.name)
    }
  }, [params?.from, stepToComplete?.name])

  const onExitPress = () => {
    void analytics.logHasExitedActivationFlow({
      from: 'stepper',
      origin_detail: 'Quit',
    })
    showQuitIdentityCheckModal()
  }
  const stepList = (
    <StepList currentStepIndex={currentStepIndex}>
      {steps.map((step, index) => (
        <StepButtonContainer key={step.name}>
          <StepButton
            step={step}
            navigateTo={getSubscriptionPropConfig(step.firstScreen, {
              type: step.firstScreenType,
            })}
            onPress={() => {
              if (step.name === IdentityCheckStep.PROFILE) {
                void recordProfileCompletionStart()
              }
              void analytics.logIdentityCheckStep(step.name)
            }}
          />
          {index === steps.length - 1 ? null : <Placeholder />}
        </StepButtonContainer>
      ))}
    </StepList>
  )

  return (
    <Page>
      <MainContainer>
        <TopContent>
          <Spacer.TopScreen />
          <TitleContainer>
            <StyledTitle1>{stepperTitle}</StyledTitle1>
          </TitleContainer>
          <Container>
            {stepperSubtitle ? <StyledSubtitle subtitle={stepperSubtitle} /> : null}
            {errorMessage ? <StyledErrorMessage errorMessage={errorMessage} /> : null}
          </Container>
          {stepList}
        </TopContent>

        <QuitButtonContainer>
          <Button
            variant="tertiary"
            color="neutral"
            icon={Invalidate}
            wording="Abandonner"
            onPress={onExitPress}
            accessibilityRole={AccessibilityRole.BUTTON}
          />
        </QuitButtonContainer>
      </MainContainer>
      <QuitIdentityCheckModal
        visible={visible}
        hideModal={hideModal}
        testIdSuffix="quit-identity-check-stepper"
      />
    </Page>
  )
}

const StyledTitle1 = styled(Typo.Title1).attrs(() => getTextSemanticAttrs(1))``

const TitleContainer = styled.View(({ theme }) => ({
  marginTop: theme.isDesktopViewport ? getSpacing(16) : theme.designSystem.size.spacing.l,
}))

const MainContainer = styled.ScrollView.attrs(({ theme }) => ({
  contentContainerStyle: {
    paddingHorizontal: theme.designSystem.size.spacing.xl,
    paddingBottom: theme.designSystem.size.spacing.xxxl,
    maxWidth: theme.contentPage.maxWidth,
    width: '100%',
    alignSelf: 'center',
    flexGrow: 1,
    justifyContent: 'space-between',
  },
}))``

const TopContent = styled.View({
  width: '100%',
})

const Placeholder = styled.View(({ theme }) => ({
  height: theme.designSystem.size.spacing.s,
}))

const StepButtonContainer = styled.View({
  alignItems: 'center',
})

const QuitButtonContainer = styled.View({
  justifyContent: 'center',
  minHeight: getSpacing(15),
})

const StyledSubtitle = ({ subtitle }: { subtitle: string }) => (
  <SubtitleContainer>
    <Typo.Body>{subtitle}</Typo.Body>
  </SubtitleContainer>
)

const StyledErrorMessage = ({ errorMessage }: { errorMessage: string }) => (
  <ErrorMessageContainer>
    <Banner type={BannerType.ERROR} label={errorMessage} />
  </ErrorMessageContainer>
)
const SubtitleContainer = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.s,
  marginBottom: theme.designSystem.size.spacing.xxl,
}))

const ErrorMessageContainer = styled(Typo.Body)(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.m,
}))

const Container = styled.View(({ theme }) => ({
  marginVertical: theme.designSystem.size.spacing.s,
}))
